import {
  Plus,
  Minus,
  CaseSensitive,
  Combine,
  ListPlus,
  ListCollapse,
  ToggleRight,
  Hash,
  Equal,
  Sigma,
  Unlink,
  ChevronsRightLeft,
  ArrowRightLeft, // New icon for assignment
} from 'lucide-react';

const getBaseType = (field) =>
  (field?.arrayItemType || field?.dataType)?.toLowerCase();

export const OPERATIONS = {
  // --- Number Operations ---
  add: {
    label: 'Add (+)',
    icon: <Plus size={16} />,
    appliesTo: ['number'],
    getOperand2Types: () => ['number'],
    getOutputSchema: () => ({ dataType: 'Number' }),
    preservesSchema: true,
  },
  subtract: {
    label: 'Subtract (-)',
    icon: <Minus size={16} />,
    appliesTo: ['number'],
    getOperand2Types: () => ['number'],
    getOutputSchema: () => ({ dataType: 'Number' }),
    preservesSchema: true,
  },

  // --- String Operations ---
  concatenate: {
    label: 'Concatenate',
    icon: <Combine size={16} />,
    appliesTo: ['string'],
    getOperand2Types: () => ['string', 'number', 'boolean'],
    getOutputSchema: () => ({ dataType: 'String' }),
    preservesSchema: false,
  },
  toUpperCase: {
    label: 'To Uppercase',
    icon: <CaseSensitive size={16} />,
    appliesTo: ['string'],
    getOperand2Types: () => [],
    getOutputSchema: () => ({ dataType: 'String' }),
    preservesSchema: true,
  },

  // --- Array Operations ---
  append: {
    label: 'Append Item',
    icon: <ListPlus size={16} />,
    appliesTo: ['array'],
    getOperand2Types: (op1Field) => [getBaseType(op1Field)],
    getOutputSchema: (op1Field) => ({
      dataType: 'Array',
      arrayItemType: op1Field.arrayItemType,
      children: op1Field.children,
    }),
    preservesSchema: true,
  },
  concatenateArrays: {
    label: 'Concatenate Arrays',
    icon: <ListCollapse size={16} />,
    appliesTo: ['array'],
    getOperand2Types: (op1Field) => ['array'],
    getOutputSchema: (op1Field) => ({
      dataType: 'Array',
      arrayItemType: op1Field.arrayItemType,
      children: op1Field.children,
    }),
    preservesSchema: true,
  },

  // --- Boolean Operations ---
  and: {
    label: 'AND',
    icon: <ToggleRight size={16} />,
    appliesTo: ['boolean'],
    getOperand2Types: () => ['boolean'],
    getOutputSchema: () => ({ dataType: 'Boolean' }),
    preservesSchema: true,
  },
  or: {
    label: 'OR',
    icon: <ToggleRight size={16} />,
    appliesTo: ['boolean'],
    getOperand2Types: () => ['boolean'],
    getOutputSchema: () => ({ dataType: 'Boolean' }),
    preservesSchema: true,
  },

  // --- Cross-Type Operations ---
  equals: {
    label: 'Equals (==)',
    icon: <Equal size={16} />,
    appliesTo: ['string', 'number', 'boolean'],
    getOperand2Types: (op1Field) => [op1Field.dataType.toLowerCase()],
    getOutputSchema: () => ({ dataType: 'Boolean' }),
    preservesSchema: false,
  },
  notEquals: {
    label: 'Not Equals (!=)',
    icon: <Unlink size={16} />,
    appliesTo: ['string', 'number', 'boolean'],
    getOperand2Types: (op1Field) => [op1Field.dataType.toLowerCase()],
    getOutputSchema: () => ({ dataType: 'Boolean' }),
    preservesSchema: false,
  },
  length: {
    label: 'Length of',
    icon: <Hash size={16} />,
    appliesTo: ['string', 'array'],
    getOperand2Types: () => [],
    getOutputSchema: () => ({ dataType: 'Number' }),
    preservesSchema: false,
  },
  toString: {
    label: 'Convert to String',
    icon: <ChevronsRightLeft size={16} />,
    appliesTo: ['number', 'boolean', 'array', 'object'],
    getOperand2Types: () => [],
    getOutputSchema: () => ({ dataType: 'String' }),
    preservesSchema: false,
  },
  isEmpty: {
    label: 'Is Empty',
    icon: <Sigma size={16} />,
    appliesTo: ['string', 'array', 'object'],
    getOperand2Types: () => [],
    getOutputSchema: () => ({ dataType: 'Boolean' }),
    preservesSchema: false,
  },
  // ⭐ NEW: Global State Assignment Operation
  assign: {
    label: 'Assign (=)',
    icon: <ArrowRightLeft size={16} />,
    appliesTo: ['string', 'number', 'boolean', 'array', 'object'], // Can apply to any data type
    getOperand2Types: (op1Field) => {
      // Operand 2 must be the exact same type as Operand 1
      return [op1Field.dataType.toLowerCase()];
    },
    getOutputSchema: (op1Field) => ({
      dataType: op1Field.dataType,
      arrayItemType: op1Field.arrayItemType,
      children: op1Field.children,
    }),
    preservesSchema: true, // Crucial for allowing global state updates
  },
};
